import {
  type MediaConfig,
  PlayerEvent,
  type PlayerInterface,
} from "@foundation-player/loader";
import { Media } from "./media_player.types";
import { Plugin } from "./media_plugin";
import { storageAvailable } from "./utils";

export type MediaPositions = {
  [key: string]: number;
};

export const MEDIA_POSITION_STORAGE_KEY = "ws-media.position";

/**
 * Plugin to save the current position.
 *
 * More about player events:
 * https://docs.player.foundation/v1/api/runtime/variables/playerevent/
 *
 * More about the player interface:
 * https://docs.player.foundation/v1/api/types/type-aliases/playerinterface/
 */
export class PluginPosition extends Plugin {
  readonly #isEnabled: boolean;
  readonly #contentId: string | undefined;
  readonly #interval: number = 5;
  #lastSavedTime: number | undefined;
  #positions: MediaPositions = {};

  public constructor(media: Media) {
    super(media);
    this.#isEnabled = storageAvailable("localStorage") && !!media.id;
    this.#contentId = media.id;
  }

  public isEnabled(): boolean {
    return this.#isEnabled;
  }

  // https://docs.player.foundation/v1/api/types/interfaces/basemediaconfig/#starttime
  public addFoundationMediaConfig(): Partial<MediaConfig> {
    this.#positions = this.#getPositions();
    return {
      startTime: this.#positions[this.#contentId!] || 0,
    };
  }

  public addEventListener(foundationPlayer: PlayerInterface): void {
    foundationPlayer.addEventListener(PlayerEvent.TimeChanged, () => {
      const isAd: boolean | undefined = foundationPlayer
        .getAdvertisingAdapter()
        ?.isAd();
      if (isAd) return;
      const newTime = foundationPlayer.getCurrentTime();
      const duration = Math.floor(foundationPlayer.getDuration("content"));
      if (newTime >= duration) {
        this.#removePosition();
      } else if (
        this.#lastSavedTime !== newTime &&
        newTime % this.#interval === 0
      ) {
        this.#savePosition(newTime);
      }
    });
  }

  #getPositions = (): MediaPositions => {
    const data = localStorage.getItem(MEDIA_POSITION_STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    } else {
      return {};
    }
  };

  #savePosition = (newTime: number) => {
    this.#positions = this.#getPositions();
    this.#positions[this.#contentId!] = newTime;
    localStorage.setItem(
      MEDIA_POSITION_STORAGE_KEY,
      JSON.stringify(this.#positions),
    );
    this.#lastSavedTime = newTime;
  };

  #removePosition = () => {
    delete this.#positions[this.#contentId!];
    localStorage.setItem(
      MEDIA_POSITION_STORAGE_KEY,
      JSON.stringify(this.#positions),
    );
    this.#lastSavedTime = 0;
  };
}
